import { useState, useEffect } from "react";
import { message, Spin } from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "./frequentlyUsedV2.css";

const dataApiUrl = process.env.REACT_APP_OTPCountPerApp;

const FrequentAppsV2 = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [chartData, setChartData] = useState([]);
  const [allAppKeys, setAllAppKeys] = useState([]);
  const [loading, setLoading] = useState(true);

  const colorMap = {
    GoldPari: "#FF0000",
    defaultColors: [
      "#38bdf8",
      "#0ea5e9",
      "#22CBCC",
      "#10b981",
      "#6366f1",
      "#8b5cf6",
      "#ec4899",
      "#f59e0b",
      "#84cc16",
      "#06b6d4",
      "#a855f7",
      "#ef4444",
    ],
  };

  const getColor = (appName, index) => {
    if (appName === "GoldPari") return colorMap.GoldPari;
    return colorMap.defaultColors[index % colorMap.defaultColors.length];
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const MAX_ITEMS = 6;
    const sorted = [...payload]
      .filter((p) => p.value > 0)
      .sort((a, b) => (b.value || 0) - (a.value || 0));
    const top = sorted.slice(0, MAX_ITEMS);
    const restCount = sorted.length - MAX_ITEMS;
    const total = sorted.reduce((sum, p) => sum + (p.value || 0), 0);

    return (
      <div className="frequent-apps-tooltip">
        <div className="frequent-apps-tooltip-title">{label}</div>
        <div className="frequent-apps-tooltip-total">Total: {total} OTPs</div>
        <div className="frequent-apps-tooltip-list">
          {top.map((p) => (
            <div key={p.name} className="frequent-apps-tooltip-row">
              <span className="frequent-apps-tooltip-dot" style={{ background: p.color }} />
              <span>{p.name}: {p.value}</span>
            </div>
          ))}
          {restCount > 0 && (
            <div className="frequent-apps-tooltip-more">+{restCount} more</div>
          )}
        </div>
      </div>
    );
  };

  const fetchChartData = async () => {
    try {
      setLoading(true);
      const response = await fetch(dataApiUrl);
      if (!response.ok) throw new Error("Failed to fetch chart data");
      const data = await response.json();

      let allApps = Array.from(
        new Set(Object.values(data).flatMap((apps) => Object.keys(apps)))
      );
      allApps = allApps.filter((app) => app !== "GoldPari");
      allApps.unshift("GoldPari");

      const formattedData = Object.keys(data).map((country) => {
        const row = { country };
        allApps.forEach((app) => {
          row[app] = data[country]?.[app] || 0;
        });
        return row;
      });

      setChartData(formattedData);
      setAllAppKeys(allApps);
    } catch (error) {
      console.error("Error fetching chart data:", error);
      messageApi.error("Failed to load chart data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChartData();
  }, []);

  return (
    <div className="frequent-apps-v2">
      {contextHolder}
      <div className="frequent-apps-v2-header">
        <h1 className="frequent-apps-v2-title">App Usage Breakdown: Manual Testers</h1>
      </div>

      <div className="frequent-apps-v2-card">
        {loading ? (
          <div className="frequent-apps-v2-loading">
            <Spin size="large" />
          </div>
        ) : chartData.length > 0 ? (
          <div className="frequent-apps-v2-chart-wrap">
            <ResponsiveContainer width="100%" height={420}>
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="country"
                  interval={0}
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={{ stroke: "#e2e8f0" }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={{ stroke: "#e2e8f0" }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="bottom"
                  wrapperStyle={{ paddingTop: 24 }}
                  iconType="square"
                  iconSize={10}
                  formatter={(value) => (
                    <span style={{ color: "#475569", fontSize: 12 }}>{value}</span>
                  )}
                />
                {allAppKeys.map((appName, idx) => (
                  <Bar
                    key={appName}
                    dataKey={appName}
                    fill={getColor(appName, idx)}
                    barSize={36}
                    radius={[4, 4, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="frequent-apps-v2-empty">No data available.</div>
        )}
      </div>
    </div>
  );
};

export default FrequentAppsV2;
